import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import Velocity from 'velocity-animate';
import Browser from '@wetransfer/concorde-browser';

let _activeInstance = null;
const _closeActiveInstance = () => {
  _activeInstance.hide();
  _activeInstance = null;
};

function getTop(anchor, target, tooltipDimensions, offset) {
  switch (anchor) {
    case 'top':
      return target.top - tooltipDimensions.height - offset;

    case 'right':
    case 'left':
      return target.top + target.height / 2 - tooltipDimensions.height / 2;

    case 'bottom':
    case 'bottom-right':
    case 'bottom-center':
    case 'bottom-left':
      return target.bottom + offset;
  }
}

function getLeft(anchor, target, tooltipDimensions, offset, clientWidth) {
  // These values come from https://github.com/WeTransfer/frontend/blob/master/app/assets/stylesheets/component/poptip.scss
  const hoverArrowWidth = 10;
  const hoverArrowRight = 12;
  const hoverArrowTranslateY = 5;

  switch (anchor) {
    case 'top':
    case 'bottom':
      return target.left + target.width / 2 - tooltipDimensions.width / 2;

    case 'right':
      return target.left - tooltipDimensions.width - offset;

    case 'left':
      return target.right + offset;

    case 'bottom-left':
      return Browser.isMobile
        ? 0
        : target.left -
            (tooltipDimensions.width -
              hoverArrowRight -
              hoverArrowWidth / 2 -
              hoverArrowTranslateY -
              target.width / 2);

    case 'bottom-right':
      return target.left - 2;

    case 'bottom-center':
      return (clientWidth - tooltipDimensions.width) / 2;
  }
}

export default class Poptip extends React.Component {
  static propTypes = {
    onClick: PropTypes.func,
    onOutsideClick: PropTypes.func,
    handleChange: PropTypes.func,
    className: PropTypes.string,
    content: PropTypes.any.isRequired,
    anchor: PropTypes.string,
    offset: PropTypes.number,
    trigger: PropTypes.oneOf(['hover', 'click', 'manual']),
    children: PropTypes.object,
    visible: PropTypes.bool,
    autoHide: PropTypes.bool,
    effects: PropTypes.object,
    unique: PropTypes.bool,
    dynamicPointer: PropTypes.bool,
    adjustWidthToParent: PropTypes.bool,
  };

  static defaultProps = {
    anchor: 'top',
    offset: 0,
    trigger: 'hover',
    visible: false,
    autoHide: true,
    dynamicPointer: false,
  };

  state = {
    content: this.props.content,
    anchor: this.props.anchor,
  };

  constructor(props) {
    super(props);

    this.rootRef = React.createRef();

    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.hide = this.hide.bind(this);
    this.position = this.position.bind(this);
    this.positionPointer = this.positionPointer.bind(this);
    this.show = this.show.bind(this);
  }

  componentDidMount() {
    // Always anchor the tooltip at the bottom if the given anchor is left or right
    if (Browser.isMobile && ['left', 'right'].indexOf(this.props.anchor) > -1) {
      this.setState({ anchor: 'bottom' });
    }

    this.hidden = true;
    this._isMounted = true; // @see https://facebook.github.io/react/blog/2015/12/16/ismounted-antipattern.html
    this.createTooltip();
    this.renderContent();
    this.addListeners();
  }

  componentDidUpdate() {
    this.renderContent();
  }

  componentWillUnmount() {
    this.removeListeners();

    this.hide(() => {
      this._isMounted = false;
      ReactDOM.unmountComponentAtNode(this.tooltip);
    });
  }

  renderContent() {
    ReactDOM.render(this.props.content, this.tooltip, () => {
      // Create and position popup, and add all listeners we need
      const { className, trigger } = this.props;
      const { anchor } = this.state;

      const classNames = ['tooltip'];
      if (trigger) {
        classNames.push('tooltip--' + trigger);
      }
      if (anchor) {
        classNames.push('tooltip--' + anchor);
      }
      if (
        this.props.content.type === 'ul' ||
        this.props.content.type.name === 'Scrollable' ||
        trigger === 'click'
      ) {
        classNames.push('tooltip--list');
      }
      if (className) {
        classNames.push(className);
      }

      this.tooltip.className = classNames.join(' ');

      if (this.hidden === false) {
        this.position();
      }
    });

    this.removeListeners();
    this.addListeners();
  }

  createTooltip() {
    this.tooltip = document.createElement('div');
    if (this.props.autoHide) {
      this.tooltip.onclick = this.hide;
    }
  }

  addListeners() {
    const { trigger, visible } = this.props;
    const element = this.rootRef.current;

    if (!element) return;

    if (visible) {
      this.show();
    } else {
      this.hide();
    }

    if (trigger === 'hover') {
      element.addEventListener('mouseover', this.show, false);
      element.addEventListener('mouseleave', this.hide, false);
    } else if (trigger === 'click') {
      element.addEventListener('click', this.toggle, false);
    }

    document.addEventListener('blur', this.handleOutsideClick, true);
    document.addEventListener('click', this.handleOutsideClick, false);
    window.addEventListener('resize', this.position, false);
    window.addEventListener('scroll', this.position, true);
  }

  removeListeners() {
    const element = this.rootRef.current;

    if (!element) return;

    element.removeEventListener('mouseover', this.show);
    element.removeEventListener('mouseleave', this.hide);
    element.removeEventListener('click', this.toggle);
    document.removeEventListener('blur', this.handleOutsideClick);
    document.removeEventListener('click', this.handleOutsideClick);
    window.removeEventListener('resize', this.position);
    window.removeEventListener('scroll', this.position);
  }

  handleOutsideClick(event) {
    if (!this._isMounted) {
      return this;
    }
    const element = this.rootRef.current;
    const { trigger, onClick, onOutsideClick } = this.props;

    // Internet Explorer loves SVG's, but it doesn't really support the same things
    // on them as we would like. To instead of `.contains` we need to use the `compareDocumentPosition`
    // method in IE. The poptip `element` can be an SVG so we will use it here. The 16 comes from
    // `Node.DOCUMENT_POSITION_CONTAINED_BY` but just to be sure with older browsers we hardcode it.
    const elementContainsTarget = element.contains
      ? element.contains(event.target)
      : element.compareDocumentPosition
      ? !!(element.compareDocumentPosition(event.target) & 16)
      : true;

    if (!elementContainsTarget && !this.tooltip.contains(event.target)) {
      if (trigger !== 'manual') {
        this.hide();
        _activeInstance = null;
      }
      onOutsideClick && onOutsideClick(event);
    } else {
      onClick && onClick(event);
    }
  }

  position() {
    const { tooltip } = this;
    const { offset, adjustWidthToParent } = this.props;
    const { anchor } = this.state;

    if (!this._isMounted) {
      return this;
    }
    // Position the tooltip based on the properties of the first children.
    // We don't want to use the properties of `this.rootRef.current` as this
    // is just a wrapper around the children and might break the layout.
    // The layout and the position of the tooltip should come from children.
    const element = this.rootRef.current.children.item(0);
    let pos = element.getBoundingClientRect();
    if (adjustWidthToParent) {
      tooltip.style.width =
        Math.round(element.parentElement.offsetWidth) + 'px';
      pos = element.parentElement.getBoundingClientRect();
    }
    const tooltipDimensions = this.tooltip.getBoundingClientRect();
    const { pageYOffset, pageXOffset } = window;
    const {
      scrollTop,
      clientTop,
      clientWidth,
      scrollLeft,
      clientLeft,
    } = document.documentElement;
    const winTop = (pageYOffset || scrollTop) - clientTop;
    const winLeft = (pageXOffset || scrollLeft) - clientLeft;

    const target = {
      top: pos.top + winTop,
      left: pos.left + winLeft,
      right: pos.right + winLeft,
      bottom: pos.bottom + winTop,
      width: pos.width,
      height: pos.height,
    };

    const top = getTop(anchor, target, tooltipDimensions, offset);
    const left = getLeft(
      anchor,
      target,
      tooltipDimensions,
      offset,
      clientWidth
    );

    tooltip.style.top = Math.round(top) + 'px';
    tooltip.style.left = Math.round(left) + 'px';
  }

  positionPointer(e) {
    let pointerLeft = 0;
    const tooltipDimensions = this.tooltip.getBoundingClientRect();
    const { clientWidth } = document.documentElement;
    pointerLeft = Math.round((e.x / clientWidth) * tooltipDimensions.width); // normalise

    const classNames = this.tooltip.className.split(' ');

    if (pointerLeft < tooltipDimensions.width / 2 - 10) {
      classNames.push('tooltip--pointer-left');
    } else if (pointerLeft > tooltipDimensions.width / 2 + 10) {
      classNames.push('tooltip--pointer-right');
    }

    this.tooltip.className = classNames.join(' ');
  }

  show(e = false) {
    if (!this.hidden) {
      return;
    }

    // If there's an active instance of a unique poptip, hide that
    if (_activeInstance !== null) {
      _closeActiveInstance();
    }

    // If this poptip is required to be unique, store it as an active instance
    if (this.props.unique) {
      _activeInstance = this;
    }

    this.hidden = false;
    document.body.appendChild(this.tooltip);
    this.transit('show');
    this.position();
    if (this.props.dynamicPointer && e) {
      this.positionPointer(e);
    }

    // Oh no, The poptip doesn't fit in the browser! Let's quickly hide it, reposition it to the bottom and show it again.
    // TODO: Make it smarter to always rotate to the next anchor. (since this only happens on the profile page now, it's fine.) & Move it back when it fits again after resize? (edge case)
    const newState = this.isOutsideViewport();
    if (newState) {
      this.adaptToViewport(e, newState);
      return;
    }

    this.props.handleChange && this.props.handleChange(true);
  }

  adaptToViewport(e, newState) {
    this.hide(() => {
      this.setState(newState, () => {
        this.renderContent();
        this.show(e);
      });
    });
  }

  isOutsideViewport() {
    // Outside viewport from right
    if (
      window.innerWidth -
        (this.tooltip.offsetWidth + this.tooltip.getBoundingClientRect().left) <
      0
    ) {
      if (Browser.isMobile || this.state.anchor === 'bottom') {
        return { anchor: 'bottom-left' };
      } else if (this.state.anchor === 'left') {
        return { anchor: 'bottom' };
      }
    }

    // Outside viewport from bottom
    if (
      this.state.anchor === 'bottom' &&
      window.innerHeight <
        this.tooltip.offsetHeight + this.tooltip.getBoundingClientRect().top
    ) {
      return { anchor: 'top' };
    }

    // Outside viewport from top
    if (
      this.state.anchor === 'top' &&
      this.tooltip.getBoundingClientRect().top < 0
    ) {
      return { anchor: 'bottom' };
    }

    return false;
  }

  hide(callback) {
    const cleanup = () => {
      if (!this.hidden) {
        document.body.removeChild(this.tooltip);
      }
      this.hidden = true;
      this.props.handleChange && this.props.handleChange(false);
      if (typeof callback === 'function') {
        callback();
      }
    };

    if (!this.hasEffects('hide')) {
      cleanup();
    } else {
      this.transit('hide', cleanup);
    }
  }

  hasEffects(type) {
    if (this.props.effects && this.props.effects[type]) {
      return true;
    }
    return false;
  }

  transit(type, complete) {
    if (this.props.effects) {
      if (this.props.effects[type] && this.props.effects[type].type) {
        Velocity(this.tooltip, this.props.effects[type].type, {
          ...this.props.effects[type].options,
          complete,
        });
      } else if (
        this.props.effects[type] &&
        this.props.effects[type].properties
      ) {
        Velocity(this.tooltip, this.props.effects[type].properties, {
          ...this.props.effects[type].options,
          complete,
        });
      }
    }
  }

  toggle = (e) => {
    e.stopPropagation();
    return this.hidden ? this.show(e) : this.hide();
  };

  render() {
    return (
      <div className="poptip" ref={this.rootRef}>
        {this.props.children}
      </div>
    );
  }
}
